import { computed } from 'vue';
import Cookies from 'js-cookie';

const DEFAULT_FEATURES = {
  kanbanEnabled: true,
  conexoesEnabled: true,
  chatsInternosEnabled: true,
  projectsEnabled: true,
  crmEnabled: true,
  appointmentsEnabled: true,
  disparadorEnabled: true,
  emailEnabled: true,
  chatbotFlowsEnabled: true,
};

const DEFAULT_PERMISSIONS = {
  kanbanAccess: true,
  conexoesAccess: true,
  chatsInternosAccess: true,
  projectsAccess: true,
  crmAccess: true,
  appointmentsAccess: true,
  disparadorAccess: true,
  emailAccess: true,
  chatbotFlowsAccess: true,
};

const SIDEBAR_ITEMS = [
  {
    key: 'kanban',
    menuId: 'cw-custom-menu-kanban',
    label: 'Kanban',
    icon: 'i-lucide-layout-dashboard',
    toggleName: 'cwCustomToggleKanban',
    featureKey: 'kanbanEnabled',
    permissionKey: 'kanbanAccess',
  },
  {
    key: 'conexoes',
    menuId: 'cw-custom-menu-conexoes',
    label: 'Conexões',
    icon: 'i-lucide-plug',
    toggleName: 'cwCustomToggleConexoes',
    featureKey: 'conexoesEnabled',
    permissionKey: 'conexoesAccess',
  },
  {
    key: 'chats',
    menuId: 'cw-custom-menu-chats',
    label: 'Chats Internos',
    icon: 'i-lucide-messages-square',
    toggleName: 'cwCustomToggleChats',
    featureKey: 'chatsInternosEnabled',
    permissionKey: 'chatsInternosAccess',
  },
  {
    key: 'projetos',
    menuId: 'cw-custom-menu-projetos',
    label: 'Projetos',
    icon: 'i-lucide-briefcase',
    toggleName: 'cwCustomToggleProjetos',
    featureKey: 'projectsEnabled',
    permissionKey: 'projectsAccess',
  },
  {
    key: 'crm',
    menuId: 'cw-custom-menu-crm',
    label: 'CRM',
    icon: 'i-lucide-users',
    toggleName: 'cwCustomToggleCrm',
    featureKey: 'crmEnabled',
    permissionKey: 'crmAccess',
  },
  {
    key: 'atendimentos',
    menuId: 'cw-custom-menu-atendimentos',
    label: 'Atendimentos',
    icon: 'i-lucide-calendar-days',
    toggleName: 'cwCustomToggleAtendimentos',
    featureKey: 'appointmentsEnabled',
    permissionKey: 'appointmentsAccess',
  },
  {
    key: 'campanhas',
    menuId: 'cw-custom-menu-campanhas',
    label: 'Disparador',
    icon: 'i-lucide-megaphone',
    toggleName: 'cwCustomToggleCampanhas',
    featureKey: 'disparadorEnabled',
    permissionKey: 'disparadorAccess',
  },
  {
    key: 'email',
    menuId: 'cw-custom-menu-email',
    label: 'Email Marketing',
    icon: 'i-lucide-mail',
    toggleName: 'cwCustomToggleEmail',
    featureKey: 'emailEnabled',
    permissionKey: 'emailAccess',
  },
  {
    key: 'chatbot',
    menuId: 'cw-custom-menu-chatbot',
    label: 'Chatbot Flows',
    icon: 'i-lucide-bot',
    toggleName: 'cwCustomToggleChatbot',
    featureKey: 'chatbotFlowsEnabled',
    permissionKey: 'chatbotFlowsAccess',
  },
];

const CONFIG_EXTRA_ITEM = {
  key: 'permissions',
  menuId: 'cw-custom-menu-permissions',
  label: 'Config Extra',
  icon: 'i-lucide-settings-2',
  toggleName: 'cwCustomTogglePermissions',
};

const sidebarAccessCache = new Map();

const getKanbanCWUrl = () => window.chatwootConfig?.kanbancwUrl || '';

const getAuthHeaders = () => {
  try {
    const raw = Cookies.get('cw_d_session_info');
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session?.['access-token']) return null;

    return {
      'access-token': session['access-token'],
      'token-type': session['token-type'] || 'Bearer',
      client: session.client || '',
      expiry: session.expiry || '',
      uid: session.uid || '',
    };
  } catch (_) {
    return null;
  }
};

const fetchBridgeJson = async (path, accountId = null) => {
  const baseUrl = getKanbanCWUrl();
  const authHeaders = getAuthHeaders();

  if (!baseUrl || !authHeaders) {
    throw new Error('missing-bridge-config');
  }

  const headers = {
    ...authHeaders,
  };

  if (accountId) {
    headers['X-Account-ID'] = String(accountId);
  }

  const response = await fetch(`${baseUrl}${path}`, {
    headers,
    credentials: 'same-origin',
  });

  if (!response.ok) {
    throw new Error(`bridge-request-failed:${response.status}`);
  }

  return response.json();
};

const buildVisibleItems = ({ features, permissions, canOpenConfigExtra }) => {
  const visibleItems = SIDEBAR_ITEMS.filter(item => {
    return (
      features[item.featureKey] !== false &&
      permissions[item.permissionKey] !== false
    );
  });

  if (canOpenConfigExtra) {
    visibleItems.push(CONFIG_EXTRA_ITEM);
  }

  return visibleItems;
};

export function useKanbanCWBridge() {
  const kanbancwUrl = computed(() => getKanbanCWUrl());
  const hasKanbanCWBridge = computed(() => Boolean(kanbancwUrl.value));

  const fetchSidebarItems = async accountId => {
    if (!hasKanbanCWBridge.value || !accountId) return [];

    const cacheKey = String(accountId);
    if (sidebarAccessCache.has(cacheKey)) {
      return sidebarAccessCache.get(cacheKey);
    }

    try {
      const [featuresResult, permissionsResult, accessResult] =
        await Promise.all([
          fetchBridgeJson('/api/features', accountId),
          fetchBridgeJson('/api/permissions/me', accountId),
          fetchBridgeJson('/api/admin/my-access', accountId),
        ]);

      const features = { ...DEFAULT_FEATURES, ...(featuresResult?.data || {}) };
      const permissions = {
        ...DEFAULT_PERMISSIONS,
        ...(permissionsResult?.data || {}),
      };
      const access = accessResult?.data || {};
      const canOpenConfigExtra = Boolean(
        access.canManageSetup ||
          access.canManageApiTokens ||
          access.canManageCompanyPermissions ||
          access.canManagePwa ||
          access.canManageDashboardScripts
      );
      const items = buildVisibleItems({
        features,
        permissions,
        canOpenConfigExtra,
      });

      sidebarAccessCache.set(cacheKey, items);
      return items;
    } catch (_) {
      const items = buildVisibleItems({
        features: DEFAULT_FEATURES,
        permissions: DEFAULT_PERMISSIONS,
        canOpenConfigExtra: false,
      });
      sidebarAccessCache.set(cacheKey, items);
      return items;
    }
  };

  const openSidebarItem = itemKey => {
    const item = [...SIDEBAR_ITEMS, CONFIG_EXTRA_ITEM].find(
      entry => entry.key === itemKey
    );
    const action = item?.toggleName ? window[item.toggleName] : null;
    if (typeof action === 'function') {
      action();
    }
  };

  const openConversationWidget = () => {
    if (typeof window.openKanbanWidgetModal === 'function') {
      window.openKanbanWidgetModal();
    }
  };

  const onPanelStateChange = handler => {
    window.addEventListener('kbcw:panel-state', handler);
    return () => window.removeEventListener('kbcw:panel-state', handler);
  };

  const onConversationStageChange = handler => {
    window.addEventListener('kbcw:conversation-stage', handler);
    return () => window.removeEventListener('kbcw:conversation-stage', handler);
  };

  return {
    hasKanbanCWBridge,
    fetchSidebarItems,
    openSidebarItem,
    openConversationWidget,
    onPanelStateChange,
    onConversationStageChange,
  };
}
